import { useMemo } from "react";
import { useAccessControl } from "../../app/use-access-control";
import { useReferenceData } from "../use-reference-data";
import { useCanFetchStaffMemberAccess } from "./use-can-fetch-staff-member-access";
import { useStaffMemberPermissions } from "./use-staff-member-permissions";

export type UseStaffEditabilityCheckOptions = {
  /** When set, role immutability uses this value and access data is not fetched. */
  staffRole?: string;
  skipPermissionsFetch?: boolean;
};

export const useStaffEditabilityCheck = (staffMemberId: string, options?: UseStaffEditabilityCheckOptions) => {
  const { profile, activeCenterId } = useAccessControl();
  const { referenceData } = useReferenceData();
  const isEditingSelf = useMemo(
    () => !!profile?.id && !!staffMemberId && profile.id === staffMemberId,
    [profile, staffMemberId]
  );
  const canFetchStaffAccess = useCanFetchStaffMemberAccess(staffMemberId);
  const shouldFetchPermissions = !options?.skipPermissionsFetch && options?.staffRole === undefined;
  const { staffMemberPermissions } = useStaffMemberPermissions(activeCenterId, staffMemberId, {
    enabled: shouldFetchPermissions && canFetchStaffAccess
  });
  const role = options?.staffRole ?? staffMemberPermissions?.role;

  const isRoleImmutable = useMemo(() => {
    const staffMemberRole = (referenceData?.roles || []).find(r => r.value === role);
    //should be defaulting to true here if role is not found?
    return !staffMemberRole || staffMemberRole.isImmutable;
  }, [referenceData, role]);

  const permissionsDisabled = useMemo(() => isEditingSelf || isRoleImmutable, [isRoleImmutable, isEditingSelf]);

  return {
    isEditingSelf,
    isRoleImmutable,
    permissionsDisabled
  };
};
